import { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { CheckCircle, Loader2, Clock, ArrowRight, Package, Truck, CreditCard, Store, Download } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface Order {
  id: string;
  total_amount: number;
  status: 'pending' | 'processing' | 'shipped' | 'delivered' | 'cancelled' | 'pending_payment';
  payment_status: 'pending' | 'paid' | 'failed' | 'refunded';
  payment_method: string;
  delivery_method: string;
  items: {
    product_id: string;
    quantity: number;
    price: number;
    product: {
      name: string;
      main_image: string;
    };
  }[];
  created_at: string;
}

export default function Confirmation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchOrder();
    } else {
      setError('ID de pedido no válido');
      setLoading(false);
    }
  }, [id]);

  const fetchOrder = async () => {
    try {
      setLoading(true);
      
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          total_amount,
          status,
          payment_status,
          payment_method,
          delivery_method,
          created_at,
          items:order_items(
            product_id,
            quantity,
            price,
            product:products(name, main_image)
          )
        `)
        .eq('id', id)
        .single();

      if (error) {
        console.error('Error al cargar el pedido:', error);
        setError('No se pudo cargar la información del pedido');
        return;
      }
      
      // Transformar los datos para que coincidan con la interfaz
      const transformedData: Order = {
        ...data,
        items: data.items.map((item: any) => ({
          product_id: item.product_id,
          quantity: item.quantity,
          price: item.price,
          product: {
            name: item.product.name,
            main_image: item.product.main_image
          }
        }))
      };

      setOrder(transformedData);
    } catch (error) {
      console.error('Error al cargar el pedido:', error);
      setError('Error al cargar la información del pedido');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = () => {
    if (order?.payment_status === 'paid') {
      return <CheckCircle className="w-16 h-16 text-green-500" />;
    } else if (order?.payment_status === 'pending') {
      return <Clock className="w-16 h-16 text-yellow-500" />;
    } else {
      return <CreditCard className="w-16 h-16 text-blue-500" />;
    }
  };

  const getStatusMessage = () => {
    if (order?.payment_status === 'paid') {
      if (order.payment_method === 'card') {
        return '¡Pago con tarjeta completado con éxito!';
      } else if (order.payment_method === 'bankTransfer') {
        return '¡Pago por transferencia bancaria completado con éxito!';
      } else if (order.payment_method === 'cash') {
        return '¡Pago en efectivo completado con éxito!';
      } else {
        return '¡Pago completado con éxito!';
      }
    } else if (order?.payment_status === 'pending') {
      return 'Pago pendiente de confirmación';
    } else {
      return 'Información de pago registrada';
    }
  };

  const getStatusDescription = () => {
    if (order?.payment_status === 'paid') {
      if (order.payment_method === 'card') {
        return 'Tu pago con tarjeta ha sido procesado correctamente. Hemos recibido tu pedido y lo estamos preparando para envío.';
      } else if (order.payment_method === 'bankTransfer') {
        return 'Tu pago por transferencia bancaria ha sido confirmado. Hemos recibido tu pedido y lo estamos preparando para envío.';
      } else if (order.payment_method === 'cash') {
        return 'Tu pago en efectivo ha sido registrado. Hemos recibido tu pedido y lo estamos preparando para envío.';
      } else {
        return 'Tu pago ha sido procesado correctamente. Hemos recibido tu pedido y lo estamos preparando para envío.';
      }
    } else if (order?.payment_status === 'pending') {
      return 'Tu pedido está pendiente de confirmación de pago. Te notificaremos cuando se complete el proceso.';
    } else {
      return 'Hemos registrado la información de tu pedido. Te mantendremos informado sobre el estado del mismo.';
    }
  };

  const getNextSteps = () => {
    if (order?.payment_status === 'paid') {
      return [
        'Recibirás un correo electrónico con los detalles de tu pedido',
        'Nuestro equipo preparará tu pedido para envío',
        'Te notificaremos cuando tu pedido sea enviado'
      ];
    } else if (order?.payment_status === 'pending') {
      return [
        'Esperaremos la confirmación de tu pago',
        'Una vez confirmado, procesaremos tu pedido',
        'Te notificaremos cuando tu pedido sea enviado'
      ];
    } else {
      return [
        'Verificaremos la información de tu pedido',
        'Te contactaremos si necesitamos información adicional',
        'Procesaremos tu pedido una vez confirmada la información'
      ];
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="max-w-7xl mx-auto px-4 py-16">
        <div className="text-center">
          <h2 className="text-2xl font-semibold mb-4">Error</h2>
          <p className="text-gray-600 mb-8">{error || 'Pedido no encontrado'}</p>
          <button
            onClick={() => navigate('/')}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            Volver al inicio
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="max-w-3xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-8 mb-8">
          <div className="text-center mb-8">
            {getStatusIcon()}
            <h1 className="text-2xl font-bold mt-4">{getStatusMessage()}</h1>
            <p className="text-gray-600 mt-2">{getStatusDescription()}</p>
          </div>

          <div className="border-t border-b py-4 my-6">
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Número de pedido:</span>
              <span className="font-medium">{order.id}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Fecha:</span>
              <span className="font-medium">{new Date(order.created_at).toLocaleDateString()}</span>
            </div>
            <div className="flex justify-between mb-2">
              <span className="text-gray-600">Total:</span>
              <span className="font-medium">Q {order.total_amount.toFixed(2)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Método de pago:</span>
              <span className="font-medium">
                {order.payment_method === 'card' && 'Tarjeta de crédito/débito'}
                {order.payment_method === 'bankTransfer' && 'Transferencia bancaria'}
                {order.payment_method === 'cash' && 'Pago en efectivo'}
              </span>
            </div>
          </div>

          <div className="mb-8">
            <h2 className="text-lg font-semibold mb-4">Próximos pasos</h2>
            <div className="space-y-4">
              {getNextSteps().map((step, index) => (
                <div key={index} className="flex items-start gap-3">
                  <div className="flex-shrink-0 w-6 h-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center font-medium text-sm">
                    {index + 1}
                  </div>
                  <p className="text-gray-700">{step}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <Link
              to={`/order/${order.id}`}
              className="flex-1 bg-blue-600 text-white px-6 py-3 rounded-lg text-center hover:bg-blue-700 font-medium flex items-center justify-center gap-2"
            >
              <Package className="w-5 h-5" />
              Ver detalles del pedido
            </Link>
            <Link
              to="/"
              className="flex-1 bg-gray-100 text-gray-700 px-6 py-3 rounded-lg text-center hover:bg-gray-200 font-medium flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-5 h-5" />
              Seguir comprando
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
} 